<template>
    <Head title="Edit Asset" />

    <div class="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-secondary-700">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Edit Asset</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Update asset information and details</p>
                        </div>
                        <InfoButton title="Editing Assets" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Edit Asset page allows you to update existing asset information in your maintenance
                                management system. You can modify asset details, change status, update financial information,
                                and adjust meter configurations.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Basic Information:</strong><br>
                                  &nbsp;&nbsp;- Asset Name: Update the descriptive name<br>
                                  &nbsp;&nbsp;- Category: Change classification if needed<br>
                                  &nbsp;&nbsp;- Location: Update physical location (required)<br>
                                  &nbsp;&nbsp;- Parent Asset: Modify hierarchy relationships<br>
                                  &nbsp;&nbsp;- Status: Update operational state<br>
                                  &nbsp;&nbsp;- Condition: Revise physical condition assessment<br>
                                  &nbsp;&nbsp;- Criticality: Adjust business impact rating<br>
                                • <strong>Equipment Details:</strong> Update manufacturer, model, serial number, and barcode<br>
                                • <strong>Financial Information:</strong> Modify purchase details, costs, and warranty dates<br>
                                • <strong>Meter Configuration:</strong> Change meter type or update current readings<br>
                                • <strong>Image Upload:</strong> Replace or add asset image (max 2MB)<br>
                                • <strong>Notes:</strong> Update additional context or specifications
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Status changes</strong> - Changing to "Down" or "Under Maintenance" may affect
                                  production schedules<br>
                                • <strong>Parent-child relationships</strong> - Ensure hierarchy makes logical sense<br>
                                • <strong>Meter readings</strong> - Current readings cannot decrease (use meter update
                                  feature instead)<br>
                                • <strong>Image replacement</strong> - Uploading a new image will replace the existing one<br>
                                • Changes are logged in the asset history for audit purposes
                            </p>
                        </InfoButton>
                    </div>
                    <Link
                        :href="route('settings.maintenance.assets.show', asset.id)"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Back to Asset
                    </Link>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6">
                <div class="space-y-8">
                    <!-- Basic Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Asset Tag (Read-only) -->
                            <div>
                                <label for="asset_tag" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Asset Tag
                                </label>
                                <input
                                    type="text"
                                    id="asset_tag"
                                    :value="asset.asset_tag"
                                    disabled
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-gray-50 dark:bg-secondary-750 text-gray-900 dark:text-secondary-400 shadow-sm sm:text-sm"
                                />
                                <p class="mt-1 text-xs text-gray-500 dark:text-secondary-400">Asset tag cannot be changed</p>
                            </div>

                            <!-- Asset Name -->
                            <div>
                                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Asset Name <span class="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    v-model="form.name"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.name }"
                                />
                                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                            </div>

                            <!-- Category -->
                            <div>
                                <label for="category_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Category <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="category_id"
                                    v-model="form.category_id"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.category_id }"
                                >
                                    <option value="">Select a category</option>
                                    <option v-for="category in categories" :key="category.id" :value="category.id">
                                        {{ category.path_name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.category_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.category_id }}</p>
                            </div>

                            <!-- Location -->
                            <div>
                                <label for="location_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Location <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="location_id"
                                    v-model="form.location_id"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.location_id }"
                                >
                                    <option value="">Select a location</option>
                                    <option v-for="location in locations" :key="location.id" :value="location.id">
                                        {{ location.path_with_type }}
                                    </option>
                                </select>
                                <p v-if="form.errors.location_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.location_id }}</p>
                            </div>

                            <!-- Parent Asset -->
                            <div>
                                <label for="parent_asset_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Parent Asset</label>
                                <select
                                    id="parent_asset_id"
                                    v-model="form.parent_asset_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.parent_asset_id }"
                                >
                                    <option value="">None (Top-level asset)</option>
                                    <option v-for="parentAsset in parentAssets" :key="parentAsset.id" :value="parentAsset.id">
                                        {{ parentAsset.asset_tag }} - {{ parentAsset.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.parent_asset_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.parent_asset_id }}</p>
                            </div>

                            <!-- Status -->
                            <div>
                                <label for="status" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Status <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="status"
                                    v-model="form.status"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.status }"
                                >
                                    <option value="operational">Operational</option>
                                    <option value="down">Down</option>
                                    <option value="maintenance">Under Maintenance</option>
                                    <option value="retired">Retired</option>
                                    <option value="disposed">Disposed</option>
                                </select>
                                <p v-if="form.errors.status" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.status }}</p>
                            </div>

                            <!-- Condition -->
                            <div>
                                <label for="condition" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Condition</label>
                                <select
                                    id="condition"
                                    v-model="form.condition"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.condition }"
                                >
                                    <option value="">Not specified</option>
                                    <option value="excellent">Excellent</option>
                                    <option value="good">Good</option>
                                    <option value="fair">Fair</option>
                                    <option value="poor">Poor</option>
                                </select>
                                <p v-if="form.errors.condition" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.condition }}</p>
                            </div>

                            <!-- Criticality -->
                            <div>
                                <label for="criticality" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Criticality <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="criticality"
                                    v-model="form.criticality"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.criticality }"
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                                <p v-if="form.errors.criticality" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.criticality }}</p>
                            </div>

                            <!-- Current Image Preview -->
                            <div v-if="asset.image" class="md:col-span-2">
                                <label class="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">Current Image</label>
                                <img
                                    :src="asset.image_url"
                                    :alt="asset.name"
                                    class="h-32 w-auto rounded-lg border border-gray-200 dark:border-secondary-700"
                                />
                            </div>

                            <!-- Image Upload -->
                            <div class="md:col-span-2">
                                <label for="image" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    {{ asset.image ? 'Replace Asset Image' : 'Upload Asset Image' }}
                                </label>
                                <input
                                    type="file"
                                    id="image"
                                    @change="handleImageUpload"
                                    accept="image/*"
                                    class="mt-1 block w-full text-sm text-gray-500
                                        file:mr-4 file:py-2 file:px-4
                                        file:rounded-md file:border-0
                                        file:text-sm file:font-semibold
                                        file:bg-blue-50 file:text-blue-700
                                        hover:file:bg-blue-100"
                                />
                                <p v-if="form.errors.image" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.image }}</p>
                                <p class="mt-1 text-xs text-gray-500 dark:text-secondary-400">Maximum file size: 2MB</p>
                            </div>
                        </div>
                    </div>

                    <!-- Equipment Details -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Equipment Details</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Manufacturer -->
                            <div>
                                <label for="manufacturer" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Manufacturer</label>
                                <input
                                    type="text"
                                    id="manufacturer"
                                    v-model="form.manufacturer"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.manufacturer }"
                                />
                                <p v-if="form.errors.manufacturer" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.manufacturer }}</p>
                            </div>

                            <!-- Model -->
                            <div>
                                <label for="model" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Model</label>
                                <input
                                    type="text"
                                    id="model"
                                    v-model="form.model"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.model }"
                                />
                                <p v-if="form.errors.model" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.model }}</p>
                            </div>

                            <!-- Serial Number -->
                            <div>
                                <label for="serial_number" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Serial Number</label>
                                <input
                                    type="text"
                                    id="serial_number"
                                    v-model="form.serial_number"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.serial_number }"
                                />
                                <p v-if="form.errors.serial_number" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.serial_number }}</p>
                            </div>

                            <!-- Barcode -->
                            <div>
                                <label for="barcode" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Barcode</label>
                                <input
                                    type="text"
                                    id="barcode"
                                    v-model="form.barcode"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.barcode }"
                                />
                                <p v-if="form.errors.barcode" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.barcode }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Financial Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Financial Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Purchase Date -->
                            <div>
                                <label for="purchase_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Purchase Date</label>
                                <input
                                    type="date"
                                    id="purchase_date"
                                    v-model="form.purchase_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.purchase_date }"
                                />
                                <p v-if="form.errors.purchase_date" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.purchase_date }}</p>
                            </div>

                            <!-- Installation Date -->
                            <div>
                                <label for="installation_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Installation Date</label>
                                <input
                                    type="date"
                                    id="installation_date"
                                    v-model="form.installation_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.installation_date }"
                                />
                                <p v-if="form.errors.installation_date" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.installation_date }}</p>
                            </div>

                            <!-- Purchase Cost -->
                            <div>
                                <label for="purchase_cost" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Purchase Cost</label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span class="text-gray-500 dark:text-secondary-400 sm:text-sm">{{ currencySymbol }}</span>
                                    </div>
                                    <input
                                        type="number"
                                        id="purchase_cost"
                                        v-model="form.purchase_cost"
                                        step="0.01"
                                        min="0"
                                        class="block w-full pl-7 rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        :class="{ 'border-red-300': form.errors.purchase_cost }"
                                    />
                                </div>
                                <p v-if="form.errors.purchase_cost" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.purchase_cost }}</p>
                            </div>

                            <!-- Current Value -->
                            <div>
                                <label for="current_value" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Current Value</label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span class="text-gray-500 dark:text-secondary-400 sm:text-sm">{{ currencySymbol }}</span>
                                    </div>
                                    <input
                                        type="number"
                                        id="current_value"
                                        v-model="form.current_value"
                                        step="0.01"
                                        min="0"
                                        class="block w-full pl-7 rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        :class="{ 'border-red-300': form.errors.current_value }"
                                    />
                                </div>
                                <p v-if="form.errors.current_value" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.current_value }}</p>
                            </div>

                            <!-- Warranty Expiry -->
                            <div>
                                <label for="warranty_expiry_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Warranty Expiry Date</label>
                                <input
                                    type="date"
                                    id="warranty_expiry_date"
                                    v-model="form.warranty_expiry_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.warranty_expiry_date }"
                                />
                                <p v-if="form.errors.warranty_expiry_date" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.warranty_expiry_date }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Meter Configuration -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Meter Configuration</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Meter Type -->
                            <div>
                                <label for="meter_type" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Meter Type <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="meter_type"
                                    v-model="form.meter_type"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.meter_type }"
                                >
                                    <option value="none">None</option>
                                    <option value="hours">Hours</option>
                                    <option value="miles">Miles</option>
                                    <option value="kilometers">Kilometers</option>
                                    <option value="cycles">Cycles</option>
                                </select>
                                <p v-if="form.errors.meter_type" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.meter_type }}</p>
                            </div>

                            <!-- Current Meter Reading -->
                            <div v-if="form.meter_type !== 'none'">
                                <label for="current_meter_reading" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Current Meter Reading
                                </label>
                                <input
                                    type="number"
                                    id="current_meter_reading"
                                    v-model="form.current_meter_reading"
                                    min="0"
                                    step="0.01"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.current_meter_reading }"
                                />
                                <p v-if="form.errors.current_meter_reading" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.current_meter_reading }}
                                </p>
                            </div>

                            <!-- Meter Unit -->
                            <div v-if="form.meter_type !== 'none'">
                                <label for="meter_unit" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Meter Unit</label>
                                <input
                                    type="text"
                                    id="meter_unit"
                                    v-model="form.meter_unit"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.meter_unit }"
                                    :placeholder="getMeterUnitPlaceholder()"
                                />
                                <p v-if="form.errors.meter_unit" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.meter_unit }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Notes -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Additional Information</h3>
                        <div>
                            <label for="notes" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Notes</label>
                            <textarea
                                id="notes"
                                v-model="form.notes"
                                rows="4"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                :class="{ 'border-red-300': form.errors.notes }"
                                placeholder="Add any additional notes about this asset..."
                            ></textarea>
                            <p v-if="form.errors.notes" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.notes }}</p>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="flex items-center justify-end space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-secondary-700">
                    <Link
                        :href="route('settings.maintenance.assets.show', asset.id)"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="form.processing">Updating...</span>
                        <span v-else>Update Asset</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useCurrency } from '@/Composables/useCurrency';

const props = defineProps({
    asset: Object,
    categories: Array,
    locations: Array,
    parentAssets: Array,
});

const toast = useToast();
const { currencySymbol } = useCurrency();

const form = useSecureForm({
    name: props.asset.name || '',
    category_id: props.asset.category_id || '',
    parent_asset_id: props.asset.parent_asset_id || '',
    location_id: props.asset.location_id || '',
    manufacturer: props.asset.manufacturer || '',
    model: props.asset.model || '',
    serial_number: props.asset.serial_number || '',
    barcode: props.asset.barcode || '',
    status: props.asset.status || 'operational',
    condition: props.asset.condition || '',
    criticality: props.asset.criticality || 'medium',
    purchase_date: props.asset.purchase_date || '',
    purchase_cost: props.asset.purchase_cost || '',
    current_value: props.asset.current_value || '',
    warranty_expiry_date: props.asset.warranty_expiry_date || '',
    installation_date: props.asset.installation_date || '',
    meter_type: props.asset.meter_type || 'none',
    current_meter_reading: props.asset.current_meter_reading || '',
    meter_unit: props.asset.meter_unit || '',
    specifications: props.asset.specifications || {},
    notes: props.asset.notes || '',
    image: null,
});

const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
        form.image = file;
    }
};

const getMeterUnitPlaceholder = () => {
    const placeholders = {
        hours: 'e.g., operating hours',
        miles: 'e.g., miles driven',
        kilometers: 'e.g., kilometers driven',
        cycles: 'e.g., operation cycles',
        none: '',
    };
    return placeholders[form.meter_type] || '';
};

const submit = () => {
    // Use form.post with _method in the data for file uploads
    form.transform((data) => ({
        ...data,
        _method: 'PUT'
    })).post(route('settings.maintenance.assets.update', props.asset.id), {
        // Backend flash message will handle success notification
        onError: () => {
            toast.error('Failed to update asset. Please check the form for errors.');
        },
    });
};
</script>
